import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // PERF (v17): swap to the selected color's image IMMEDIATELY on selection,
    // before the section-rendering fetch resolves. Purely additive + guarded:
    // if the target slide can't be resolved, it does nothing and the existing
    // variant:update flow below still updates the gallery as before.
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles the early 'variant:selected' event (fired on click, before the fetch)
   * to move the slideshow to the selected color's image instantly.
   *
   * GUARD-RAIL: if anything required is missing (map, media id, matching slide),
   * this returns without side effects, so we safely fall back to #handleVariantUpdate.
   *
   * @param {import('./events').VariantSelectedEvent} event - The variant:selected event.
   */
  #handleVariantSelected = (event) => {
    try {
      const optionValueId = event?.detail?.resource?.id;
      if (!optionValueId) return;

      const slideshow = this.slideshow;
      const slides = slideshow?.slides;
      if (!slideshow || !slides || !slides.length) return;

      const mediaId = this.#mediaIdForOptionValue(optionValueId);
      if (!mediaId) return;

      const index = slides.findIndex((slide) =>
        slide.querySelector(`.product-media[data-media-id="${mediaId}"]`)
      );
      if (index < 0) return;

      // Instant, non-animated jump to the matching slide.
      slideshow.select(index, undefined, { animate: false });
    } catch (error) {
      // Never let the instant-swap optimization break the page; the fetch path still runs.
      console.warn('Instant variant image swap skipped:', error);
    }
  };

  /**
   * Resolves the media id associated with an option value id using the JSON map
   * emitted by the variant picker (data-variant-media-map).
   *
   * @param {string} optionValueId - The selected option value id.
   * @returns {string | undefined} The media id, or undefined if not found.
   */
  #mediaIdForOptionValue(optionValueId) {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const mapScript = scope.querySelector('script[data-variant-media-map]');
    const raw = mapScript?.textContent?.trim();
    if (!raw) return undefined;

    let map;
    try {
      map = JSON.parse(raw);
    } catch (error) {
      return undefined;
    }

    const mediaId = map?.[optionValueId];
    return mediaId != null ? String(mediaId) : undefined;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
